import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import * as React from 'react';

import { CROSS_PITCH_ROWS, CrossPitchRow } from '../graphql/queries/cross-pitches';
import { useSlides } from '../hooks/use-candidate-slides';
import { dataPanelContainerStyles } from './data-panel';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';

type Column = 'recruiter' | 'count';

const columns: Array<DataPanelColumn<Column>> = [
    { id: 'recruiter', label: 'Recruiter' },
    { id: 'count', label: 'Cross Pitches' }
];

const PAGE_SIZE = 200;

interface LeaderboardRow {
    userId: string;
    recruiter: string;
    count: number;
    rows: CrossPitchRow[];
}

const listDialogStyles = css`
    .MuiDialogContent-root {
        padding: 0;

        .MuiListItem-root {
            padding-left: 24px;

            .stage {
                font-size: 14px;
            }
        }
    }
`;

export const CrossPitches: React.FC = () => {
    const theme = useTheme();
    const { setList } = useSlides();
    const [selected, setSelected] = React.useState<LeaderboardRow | null>(null);

    const { data } = useQuery<{ crossPitches: CrossPitchRow[] }>(CROSS_PITCH_ROWS);

    const leaderboard: LeaderboardRow[] = React.useMemo(() => {
        if (!data?.crossPitches) return undefined;
        const map = new Map<string, LeaderboardRow>();
        data?.crossPitches?.forEach((row) => {
            const userId = row.assignee;
            const name = row.user?.name || row.user?.username || row.assignee || 'Unknown';
            const existing = map.get(userId);
            if (existing) {
                existing.count += 1;
                existing.rows.push(row);
            } else {
                map.set(userId, { userId, recruiter: name, count: 1, rows: [row] });
            }
        });
        return Array.from(map.values());
    }, [data?.crossPitches]);

    const getCellValue = (column: Column) => (row: LeaderboardRow) => {
        switch (column) {
            case 'recruiter':
                return row.recruiter;
            case 'count':
                return row.count;
            default:
                return null;
        }
    };

    const handleOpenDetails = (row: LeaderboardRow) => () => setSelected(row);
    const handleCloseDetails = () => setSelected(null);

    const getCellRenderer = {
        count: (value: CellValue, row: LeaderboardRow) => (
            <Tooltip title="View details">
                <span className="clickable-cell" onClick={handleOpenDetails(row)}>
                    {(value as number).toLocaleString()}
                </span>
            </Tooltip>
        ),
        recruiter: (value: CellValue) => <span>{value as string}</span>
    };

    const detailsListItems = React.useMemo(() => {
        if (!selected) return null;
        const sortedRows = [...selected.rows].sort((a, b) => {
            const an = (a.candidate?.person?.name || '').toLowerCase();
            const bn = (b.candidate?.person?.name || '').toLowerCase();
            if (an < bn) return -1;
            if (an > bn) return 1;
            const aj = (a.candidate?.job?.title || '').toLowerCase();
            const bj = (b.candidate?.job?.title || '').toLowerCase();
            if (aj < bj) return -1;
            if (aj > bj) return 1;
            return 0;
        });
        return sortedRows.map((r, idx) => {
            const person = r.candidate?.person?.name || r.personId;
            const job = r.candidate?.job?.title || r.jobId;
            const client = r.candidate?.job?.client?.name || '';
            const stage = r.candidate?.job_stage?.label || '';
            const clientJob = client && job ? `${client} › ${job}` : client || job;
            const key = `${r.personId}-${r.jobId}-${idx}`;
            const handleOpenSlides = () => {
                const list = sortedRows.map((x) => ({ personId: x.personId, jobId: x.jobId, selectedTab: 'profile' }));
                setList(list, { personId: r.personId, jobId: r.jobId, selectedTab: 'profile' });
            };
            return (
                <ListItem key={key} button={true} onClick={handleOpenSlides}>
                    <ListItemText primary={person} secondary={clientJob} />
                    <span className="stage">{stage}</span>
                </ListItem>
            );
        });
    }, [selected, setList]);

    const detailsDialog = selected ? (
        <Dialog open={!!selected} onClose={handleCloseDetails} fullWidth={true} maxWidth="md" css={listDialogStyles}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    {selected.recruiter}
                </Typography>
            </DialogTitle>
            <DialogContent dividers={true}>
                <List>{detailsListItems}</List>
            </DialogContent>
        </Dialog>
    ) : null;

    const explanationContainerStyle: React.CSSProperties = {
        backgroundColor: theme.palette.background.paper,
        borderRadius: '4px',
        marginTop: '24px',
        padding: '16px'
    };

    return (
        <div css={dataPanelContainerStyles(theme)}>
            <div className="data-panel">
                <DataPanel
                    columns={columns}
                    data={leaderboard}
                    getCellValue={getCellValue}
                    getCellRenderer={getCellRenderer}
                    sortableColumns={['count', 'recruiter']}
                    initialSortColumn="count"
                    initialSortDirection="desc"
                    hidePagination={true}
                    pageSize={PAGE_SIZE}
                />
            </div>
            <div style={explanationContainerStyle}>
                <Typography variant="body2" color="textSecondary">
                    <strong>Cross Pitch Criteria:</strong> A candidate counts as a cross pitch when they appear on a job
                    where they reached "Rocket Screen Complete" (stage 13+) within the last 60 days, and have progressed
                    to "Awaiting Client Feedback" (stage 19) on a different job during the current month. Multiple jobs
                    are allowed and will be counted. The leaderboard shows the total count of job submissions for each
                    recruiter's cross-pitched candidates.
                </Typography>
            </div>
            {detailsDialog}
        </div>
    );
};
